/**
 * Tasks for people (development plan §18.3, §19.4 amendment 19 part 1): one queue of studio work with an owner.
 * Agents (N2) will take tasks from the same queue as workspace members, so nothing here assumes the assignee is a
 * person: an assignee is a member id.
 */
import { autoApprovalSchedule, type ApprovalRules } from './approvals.js';
import { can, type Role } from './permissions.js';
import type { SourceKind } from './pipeline.js';

export const TASK_TYPES = ['ingest.check', 'edit.apply_changes', 'review.precheck', 'publish.plan', 'customer.chase', 'customer.reply', 'ops.diagnose', 'custom'] as const;
export type TaskType = (typeof TASK_TYPES)[number];

export const TASK_STATUSES = ['todo', 'in_progress', 'needs_you', 'done', 'cancelled', 'failed'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];
/** Statuses that still need someone: the inbox columns To do · In progress · Needs you. */
export const OPEN_TASK_STATUSES = ['todo', 'in_progress', 'needs_you'] as const satisfies readonly TaskStatus[];
export const isOpenTask = (s: TaskStatus) => (OPEN_TASK_STATUSES as readonly TaskStatus[]).includes(s);

export const TASK_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

/** Event kinds on a task. The last five are reserved for agents (N2) and never written by N1. */
export const TASK_EVENT_KINDS = ['created', 'comment', 'status', 'assign', 'trigger', 'edit', 'tool_call', 'tool_result', 'proposal', 'accept', 'reject'] as const;
export type TaskEventKind = (typeof TASK_EVENT_KINDS)[number];

/** Who a trigger hands its task to: the project's default producer or editor, or nobody (unassigned). */
export const TASK_ASSIGN_ROLES = ['producer', 'editor', 'none'] as const;
export type TaskAssignRole = (typeof TASK_ASSIGN_ROLES)[number];

/** Pipeline events that open a task for a person (amendment 19 part 1, item 3). */
export const TASK_TRIGGERS = ['session_needs_hand', 'changes_requested', 'customer_comment', 'approval_deadline', 'export_failed', 'publication_held'] as const;
export type TaskTrigger = (typeof TASK_TRIGGERS)[number];

export interface TriggerSetting {
  enabled: boolean;
  assign: TaskAssignRole;
  /** `approval_deadline` only: open the task this many hours before the automatic approval. */
  hoursBefore?: number;
}

export interface TaskSettings {
  /** The studio's time zone: the publish slot date and the default for digests. */
  timeZone: string;
  triggers: Record<TaskTrigger, TriggerSetting>;
}

/** The task type, default assignee and priority behind each trigger. */
export const TRIGGER_META: Record<TaskTrigger, { type: TaskType; assign: TaskAssignRole; priority: TaskPriority }> = {
  session_needs_hand: { type: 'ingest.check', assign: 'editor', priority: 'high' },
  changes_requested: { type: 'edit.apply_changes', assign: 'editor', priority: 'high' },
  customer_comment: { type: 'customer.reply', assign: 'producer', priority: 'normal' },
  approval_deadline: { type: 'customer.chase', assign: 'producer', priority: 'high' },
  export_failed: { type: 'ops.diagnose', assign: 'producer', priority: 'normal' },
  publication_held: { type: 'publish.plan', assign: 'producer', priority: 'urgent' },
};

export const DEFAULT_DEADLINE_HOURS = 12;

export const DEFAULT_TASK_SETTINGS: TaskSettings = {
  timeZone: 'Asia/Jerusalem',
  triggers: Object.fromEntries(
    TASK_TRIGGERS.map((t) => [t, { enabled: true, assign: TRIGGER_META[t].assign, ...(t === 'approval_deadline' ? { hoursBefore: DEFAULT_DEADLINE_HOURS } : {}) }]),
  ) as Record<TaskTrigger, TriggerSetting>,
};

/** Stored settings (any partial shape, possibly from an older version) merged over the defaults. */
export function resolveTaskSettings(stored: unknown): TaskSettings {
  const s = (stored && typeof stored === 'object' ? stored : {}) as { timeZone?: unknown; triggers?: Record<string, unknown> };
  const triggers = {} as Record<TaskTrigger, TriggerSetting>;
  for (const t of TASK_TRIGGERS) {
    const d = DEFAULT_TASK_SETTINGS.triggers[t];
    const v = (s.triggers?.[t] && typeof s.triggers[t] === 'object' ? s.triggers[t] : {}) as Partial<TriggerSetting>;
    triggers[t] = {
      enabled: typeof v.enabled === 'boolean' ? v.enabled : d.enabled,
      assign: (TASK_ASSIGN_ROLES as readonly string[]).includes(v.assign as string) ? (v.assign as TaskAssignRole) : d.assign,
      ...(t === 'approval_deadline' ? { hoursBefore: typeof v.hoursBefore === 'number' && v.hoursBefore > 0 && v.hoursBefore <= 168 ? v.hoursBefore : DEFAULT_DEADLINE_HOURS } : {}),
    };
  }
  return { timeZone: typeof s.timeZone === 'string' && isTimeZone(s.timeZone) ? s.timeZone : DEFAULT_TASK_SETTINGS.timeZone, triggers };
}

export function isTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

// ───────────────────────────── permissions ─────────────────────────────

export interface TaskChange {
  status?: TaskStatus;
  /** The new assignee (null = unassign); undefined = unchanged. */
  assigneeMemberId?: string | null;
  /** Title, brief, priority or due date. */
  details?: boolean;
}

/**
 * May `role` (member `memberId`) make this change to a task? Producers, admins and owners may change anything
 * (`tasks.assign`). Editors (`tasks.work`) may work on tasks assigned to them or unassigned: move their status, take
 * an unassigned task themselves, or hand their own task back (unassign). Viewers and client reviewers never.
 */
export function canChangeTask(role: Role, memberId: string, task: { assigneeMemberId: string | null; createdByMemberId: string | null }, change: TaskChange): boolean {
  if (!can(role, 'tasks.work')) return false;
  if (can(role, 'tasks.assign')) return true;
  const mine = task.assigneeMemberId === memberId;
  const free = task.assigneeMemberId === null;
  if (change.assigneeMemberId !== undefined) {
    const takes = free && change.assigneeMemberId === memberId;
    const handsBack = mine && change.assigneeMemberId === null;
    if (!takes && !handsBack && !(mine && change.assigneeMemberId === memberId)) return false;
  }
  if (change.details && !(mine || task.createdByMemberId === memberId)) return false;
  if (change.status !== undefined && !(mine || free)) return false;
  return true;
}

// ───────────────────────────── triggers ─────────────────────────────

export type NeedsHandReason = 'manual_clap' | 'no_camera' | 'no_mic' | 'renders_failed' | 'session_failed';

/**
 * Why a processed session needs a person (`session_needs_hand`): a source whose sync needs a manual clap, no
 * camera, no audio source (mic or mix), failed renders, or a failed run. Empty = it needs nobody.
 */
export function sessionNeedsHand(s: { status: string; error: string | null }, sources: { kind: SourceKind; uploaded: boolean; needsManualClap: boolean }[]): NeedsHandReason[] {
  if (s.status === 'failed') return ['session_failed'];
  if (s.status !== 'ready') return [];
  const up = sources.filter((x) => x.uploaded);
  const out: NeedsHandReason[] = [];
  if (up.some((x) => x.needsManualClap)) out.push('manual_clap');
  if (!up.some((x) => x.kind === 'camera' || x.kind === 'program')) out.push('no_camera');
  if (!up.some((x) => x.kind === 'mic' || x.kind === 'mix')) out.push('no_mic');
  if (s.error && /render\(s\) failed/.test(s.error)) out.push('renders_failed');
  return out;
}

/**
 * `approval_deadline`: is the video close enough to its automatic customer approval to chase the customer?
 * True from `hoursBefore` hours before the auto-approve time until that time.
 */
export function deadlineNear(rules: ApprovalRules, clientSince: Date | null, now: Date, hoursBefore: number): { due: boolean; autoApproveAt: Date | null } {
  if (!clientSince) return { due: false, autoApproveAt: null };
  const { autoApproveAt } = autoApprovalSchedule(rules, clientSince);
  if (!autoApproveAt) return { due: false, autoApproveAt: null };
  const from = autoApproveAt.getTime() - hoursBefore * 3600_000;
  return { due: now.getTime() >= from && now.getTime() < autoApproveAt.getTime(), autoApproveAt };
}

/** The local calendar date of `at` in `timeZone`, as YYYY-MM-DD. */
export function localDate(at: Date, timeZone: string): string {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(at).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
}

/** The local hour (0–23) of `at` in `timeZone`. */
export function localHourOf(at: Date, timeZone: string): number {
  const h = new Intl.DateTimeFormat('en-US', { timeZone, hour: '2-digit', hourCycle: 'h23' }).formatToParts(at).find((x) => x.type === 'hour')?.value;
  return Number(h) % 24;
}

/** Dedupe keys: at most one open task per key (a partial unique index on open tasks; amendment 19 part 1, item 3). */
export const taskKey = {
  sessionNeedsHand: (sessionId: string, run: number) => `session_needs_hand:${sessionId}:run${run}`,
  changesRequested: (editId: string, version: number) => `changes_requested:${editId}:v${version}`,
  customerComment: (editId: string) => `customer_comment:${editId}`,
  approvalDeadline: (editId: string, version: number) => `approval_deadline:${editId}:v${version}`,
  exportFailed: (exportId: string) => `export_failed:${exportId}`,
  publicationHeld: (publicationId: string, day: string) => `publication_held:${publicationId}:${day}`,
};

/**
 * Triggers that describe a lasting condition (a session that needs a clap, a failed export, a video near its deadline,
 * a held post): they open at most one task per key ever, so the scheduler's re-check never recreates a task someone
 * closed. Event triggers (`changes_requested`, `customer_comment`) open a new task when the same key comes back after
 * the previous one was closed; while one is open, the repeat is added to it.
 */
export const ONCE_TRIGGERS: readonly TaskTrigger[] = ['session_needs_hand', 'export_failed', 'approval_deadline', 'publication_held'];

// ───────────────────────────── presence ─────────────────────────────

/** How long a heartbeat keeps someone "here" (seconds) and how often the web app sends one. */
export const PRESENCE_TTL_S = 30;
export const PRESENCE_HEARTBEAT_S = 10;

export interface PresenceEntry {
  memberId: string;
  name: string;
  editingEditId: string | null;
}

/** The other people editing the video `editId` right now (the "two people editing" warning). */
export function presenceConflicts(entries: PresenceEntry[], me: string, editId: string | null): PresenceEntry[] {
  if (!editId) return [];
  return entries.filter((e) => e.memberId !== me && e.editingEditId === editId);
}
